import { throwIfFronteggFailed } from "@/lib/auth/auth.utils";
import { useAuth, useTenantsState } from "@frontegg/react";
import {
  mergeUsersWithRoles,
  parseFronteggUsersPayload,
  parseFronteggUsersRolesPayload,
  type FronteggIdentityRole,
  type FronteggIdentityUser,
} from "./users.utils";
import {
  parseFronteggRolesPayload,
  type AuthActorRoleRef,
} from "./users.roles";

export const AUTH_USERS_QUERY_KEY = ["frontegg", "identity", "users"] as const;
export const AUTH_ROLES_QUERY_KEY = ["frontegg", "identity", "roles"] as const;

export function getIdentityBaseUrl() {
  const baseUrl = import.meta.env.VITE_FRONTEGG_BASE_URL;
  if (!baseUrl) {
    throw new Error("VITE_FRONTEGG_BASE_URL is not defined");
  }
  return `${baseUrl}/frontegg/identity`;
}

export function buildIdentityHeaders(
  token: string,
  tenantId?: string,
): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (tenantId) {
    headers["frontegg-tenant-id"] = tenantId;
  }
  return headers;
}

export async function parseJsonResponse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return null;
  }
  return JSON.parse(text) as unknown;
}

/**
 * Loads role-bearing user rows for the given IDs via users/v2 (includes `roles`).
 * Falls back to users/v3/roles + roles catalog when v2 returns no role data.
 */
export async function fetchUsersRolesForIds(
  token: string,
  tenantId: string | undefined,
  userIds: string[],
): Promise<FronteggIdentityUser[]> {
  const params = new URLSearchParams({
    ids: userIds.join(","),
    _limit: String(Math.max(userIds.length, 1)),
  });
  const response = await fetch(
    `${getIdentityBaseUrl()}/resources/users/v2?${params}`,
    { headers: buildIdentityHeaders(token, tenantId) },
  );
  await throwIfFronteggFailed(response, "Frontegg user roles request failed");
  const json: unknown = await response.json();
  const detailed = parseFronteggUsersPayload(json, tenantId);
  if (detailed.some((user) => user.roles.length > 0)) {
    return detailed;
  }

  const roleParams = new URLSearchParams();
  for (const userId of userIds) {
    roleParams.append("ids", userId);
  }
  const rolesResponse = await fetch(
    `${getIdentityBaseUrl()}/resources/users/v3/roles?${roleParams}`,
    { headers: buildIdentityHeaders(token, tenantId) },
  );
  await throwIfFronteggFailed(
    rolesResponse,
    "Frontegg user roles request failed",
  );
  const rolesJson: unknown = await rolesResponse.json();
  const assignments = parseFronteggUsersRolesPayload(rolesJson, tenantId);
  if (assignments.length === 0) {
    return detailed;
  }
  const catalog = await fetchFronteggRolesCatalog(token, tenantId);
  return mergeUsersWithRoles(
    detailed.length > 0
      ? detailed
      : userIds.map((id) => ({
        id,
        name: id,
        email: id,
        roles: [],
        isDisabled: false,
      })),
    assignments,
    catalog,
  );
}

export function useIdentityAuth() {
  const { user } = useAuth();
  const { activeTenant } = useTenantsState();

  const token = user?.accessToken;
  const userId = user?.id;
  const tenantId = activeTenant?.tenantId ?? user?.tenantId;
  const actorRoles = (user?.roles ?? []) as AuthActorRoleRef[];

  return { actorRoles, tenantId, token, userId };
}

export async function fetchFronteggRolesCatalog(
  token: string,
  tenantId: string | undefined,
): Promise<FronteggIdentityRole[]> {
  const response = await fetch(`${getIdentityBaseUrl()}/resources/roles/v1`, {
    headers: buildIdentityHeaders(token, tenantId),
  });
  await throwIfFronteggFailed(response, "Frontegg roles request failed");
  const json: unknown = await response.json();
  return parseFronteggRolesPayload(json);
}
